<script setup lang="ts">
import {computed, onBeforeUnmount, watch} from 'vue'

type CloseReason = 'overlay' | 'esc' | 'button' | 'program'

const props = withDefaults(defineProps<{
  modelValue: boolean
  title?: string
  closeOnOverlay?: boolean
  closeOnEsc?: boolean
  lockScroll?: boolean
}>(), {
  closeOnOverlay: false,
  closeOnEsc: true,
  lockScroll: true,
})

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
  (e: 'open'): void
  (e: 'close', reason: CloseReason): void
}>()

const isOpen = computed(() => props.modelValue)

function setBodyLock(locked: boolean) {
  if (!props.lockScroll) return
  document.body.style.overflow = locked ? 'hidden' : ''
}

function close(reason: CloseReason = 'program') {
  emit('close', reason)
  emit('update:modelValue', false)
}

function onOverlayClick() {
  if (props.closeOnOverlay) close('overlay')
}

function onKeydown(e: KeyboardEvent) {
  if (!props.closeOnEsc) return
  if (e.key === 'Escape' && isOpen.value) close('esc')
}

watch(isOpen, (v) => {
  if (v) {
    emit('open')
    setBodyLock(true)
    window.addEventListener('keydown', onKeydown)
  } else {
    setBodyLock(false)
    window.removeEventListener('keydown', onKeydown)
  }
}, {immediate: true})

onBeforeUnmount(() => {
  setBodyLock(false)
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="isOpen" class="m-overlay" @click="onOverlayClick">
        <div class="m-card" role="dialog" aria-modal="true" @click.stop>
          <header class="m-header" v-if="title">
            <h3 class="m-title">{{ title }}</h3>
          </header>

          <div class="m-body">
            <slot/>
          </div>

          <footer v-if="$slots.footer" class="m-footer">
            <slot name="footer"/>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.m-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 9999;
}

.m-card {
  width: min(560px, 100%);
  background: var(--color-background);
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, .25);
  overflow: hidden;
}

.m-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  margin: 0 16px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-label);
}

.m-title {
  margin: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
}

.m-x {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: var(--color-white);
  cursor: pointer;
  font-size: 20px;
  line-height: 0;
}

.m-body {
  padding: 16px;
}

.m-footer {
  padding: 14px 0;
  margin: 0 16px;
  border-top: 1px solid var(--color-border);
  display: flex;
  gap: 10px;
}

.modal-fade-enter-active, .modal-fade-leave-active {
  transition: opacity .18s ease;
}

.modal-fade-enter-from, .modal-fade-leave-to {
  opacity: 0;
}
</style>